import { useMemo } from 'react'

import { TableFilters } from '@circlefin/components'
import { usePermission } from '@circlefin/permissions/react'
import { useSettlementCreditLine } from '@features/settlement-advance/hooks/settlement-credit-line'
import { FX_LAUNCH_ENABLED, SETTLEMENT_ADVANCE } from '@services/permissions'
import {
  SettlementCreditLineType,
  TransactionType as TransactionTypeEnum,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import type { SelectListItem } from '@circlefin/components/lib/SelectList'
import type { FilterBaseProps } from '@circlefin/components/lib/TableFilters/types'

/**
 * Payment Transaction Type Filter.
 */
export const TransactionType: React.FC<FilterBaseProps> = (props) => {
  const { t } = useTranslation('payments')
  const { settlementCreditLineType } = useSettlementCreditLine()
  const [, { isAuthorized: fxEnabled }] = usePermission(FX_LAUNCH_ENABLED)
  const [, { isAuthorized: settlementAdvanceEnabled }] =
    usePermission(SETTLEMENT_ADVANCE)

  const settlementCreditLabel = useMemo(() => {
    switch (settlementCreditLineType) {
      case SettlementCreditLineType.lineOfCredit:
        return t('activity.types.lineOfCredit')
      case SettlementCreditLineType.settlementAdvance:
        return t('activity.types.settlementAdvance')
    }
  }, [settlementCreditLineType, t])

  const items: Array<SelectListItem<TransactionTypeEnum[] | undefined>> =
    useMemo(
      () => [
        {
          value: undefined,
          label: t('activity.types.allTypes'),
        },
        {
          value: [
            TransactionTypeEnum.wire_receive,
            TransactionTypeEnum.signet_receive,
            TransactionTypeEnum.sen_receive,
            TransactionTypeEnum.cbit_receive,
            TransactionTypeEnum.cubix_receive,
            TransactionTypeEnum.pix_receive,
            TransactionTypeEnum.xpay_receive,
            TransactionTypeEnum.rtp_receive,
            TransactionTypeEnum.rtgs_receive,
            TransactionTypeEnum.sepa_receive,
            TransactionTypeEnum.sepa_instant_receive,
            TransactionTypeEnum.book_transfer_receive,
          ],
          label: t(
            `${
              fxEnabled ? 'activity.types.mints' : 'activity.types.bankDeposits'
            }`,
          ),
        },
        {
          value: [TransactionTypeEnum.onchain_receive],
          label: t(
            `${
              fxEnabled
                ? 'activity.types.receiveOnChain'
                : 'activity.types.blockchainDeposits'
            }`,
          ),
        },
        {
          value: [TransactionTypeEnum.onchain_send],
          label: t('activity.types.sendOnChain'),
        },
        {
          value: [
            TransactionTypeEnum.wire_withdrawal,
            TransactionTypeEnum.signet_withdrawal,
            TransactionTypeEnum.sen_withdrawal,
            TransactionTypeEnum.cbit_withdrawal,
            TransactionTypeEnum.cubix_withdrawal,
            TransactionTypeEnum.pix_withdrawal,
            TransactionTypeEnum.xpay_withdrawal,
            TransactionTypeEnum.rtp_withdrawal,
            TransactionTypeEnum.rtgs_withdrawal,
            TransactionTypeEnum.sepa_withdrawal,
            TransactionTypeEnum.sepa_instant_withdrawal,
            TransactionTypeEnum.book_transfer_withdrawal,
          ],
          label: t(
            `${
              fxEnabled
                ? 'activity.types.redeems'
                : 'activity.types.withdrawals'
            }`,
          ),
        },
        {
          value: [
            TransactionTypeEnum.trade_withdrawal,
            TransactionTypeEnum.trade_mint,
            TransactionTypeEnum.convert_mint,
            TransactionTypeEnum.convert_withdrawal,
          ],
          label: t('activity.types.trades'),
        },
        {
          value: [
            TransactionTypeEnum.batch_trades_withdrawal,
            TransactionTypeEnum.batch_trades_mint,
          ],
          label: t('activity.types.tradeSettlements'),
        },
        {
          value: [
            TransactionTypeEnum.redemption_fee_gross,
            TransactionTypeEnum.redemption_fee_net,
          ],
          label: t('activity.types.redemptionFees'),
        },
        ...(settlementAdvanceEnabled
          ? [
              {
                value: [
                  TransactionTypeEnum.settlement_credit_fee,
                  TransactionTypeEnum.settlement_credit_receive,
                  TransactionTypeEnum.settlement_credit_overrepayment_refund,
                  TransactionTypeEnum.settlement_credit_overrepayment_refund_transfer,
                  TransactionTypeEnum.credit_crypto_repayment,
                  TransactionTypeEnum.credit_crypto_repayment_receive,
                ],
                label: settlementCreditLabel,
              },
            ]
          : []),
      ],
      [t, fxEnabled, settlementAdvanceEnabled, settlementCreditLabel],
    )

  return <TableFilters.Dropdown {...props} items={items} />
}
